import { useEffect, useRef, useState } from 'react';
import { MdIcon } from './Icon/MdIcon';
import './WaitingForParticipants.css';

interface WaitingForParticipantsProps {
  /** Copies the invite link; resolves true once it is on the clipboard. */
  onCopyInviteLink: () => Promise<boolean> | void;
  onClose: () => void;
}

/**
 * "Your meeting is live": shown to the host while nobody else has joined,
 * with the invite link one click away. A Material 3 card (surface-container-
 * high, large shape, level 3) floating over the call, in the app's tokens
 * rather than the Calls SDK's.
 */
function WaitingForParticipants({
  onCopyInviteLink,
  onClose,
}: WaitingForParticipantsProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const copy = async () => {
    const ok = await onCopyInviteLink();
    if (ok === false) return;
    setCopied(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section
      className="waiting-for-participants"
      role="dialog"
      aria-labelledby="waiting-title"
      aria-describedby="waiting-subtitle">
      <header className="waiting-header">
        <h2 className="waiting-title" id="waiting-title">
          Your meeting is live
        </h2>
        <button
          type="button"
          className="md-icon-button"
          onClick={onClose}
          aria-label="Close">
          <MdIcon name="close" />
        </button>
      </header>
      <p className="waiting-subtitle" id="waiting-subtitle">
        You're the only one here. Share the invite link so others can join.
      </p>
      <button type="button" className="copy-invite-button" onClick={copy}>
        <MdIcon name={copied ? 'check' : 'content_copy'} size={18} />
        {copied ? 'Link copied' : 'Copy invite link'}
      </button>
    </section>
  );
}

export default WaitingForParticipants;
